import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import { TransactionComment } from "@/jenesys-types/jenesys-entities/config";

interface CommentWithDocumentId extends TransactionComment {
  document_id: string;
}

export interface TransactionCommentsState {
  comments: Array<CommentWithDocumentId>;
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  error?: AppError;
  editCommentId?: string;
  deleteCommentId?: string;
}

export interface LoadCommentsPayload {
  documentId: string;
  companyId: Id;
}

export type LoadCommentsAction = PayloadAction<LoadCommentsPayload>;

export interface LoadCommentsSuccessPayload {
  documentId: string;
  comments: Array<TransactionComment>;
}

export type LoadCommentsSuccessAction =
  PayloadAction<LoadCommentsSuccessPayload>;

export interface LoadCommentsFailurePayload {
  documentId: string;
  error: AppError;
}

export type LoadCommentsFailureAction =
  PayloadAction<LoadCommentsFailurePayload>;

export interface AddCommentPayload {
  documentId: string;
  companyId: Id;
  comment: string;
}

export type AddCommentAction = PayloadAction<AddCommentPayload>;

export interface AddCommentSuccessPayload {
  documentId: string;
  comment: TransactionComment;
}

export type AddCommentSuccessAction = PayloadAction<AddCommentSuccessPayload>;

export interface AddCommentFailurePayload {
  documentId: string;
  error: AppError;
}

export type AddCommentFailureAction = PayloadAction<AddCommentFailurePayload>;

export interface EditCommentPayload {
  documentId: string;
  companyId: Id;
  commentId: string;
  comment: string;
}

export type EditCommentAction = PayloadAction<EditCommentPayload>;

export interface EditCommentSuccessPayload {
  documentId: string;
  comment: TransactionComment;
}

export type EditCommentSuccessAction = PayloadAction<EditCommentSuccessPayload>;

export interface EditCommentFailurePayload {
  documentId: string;
  commentId: string;
  error: AppError;
}

export type EditCommentFailureAction = PayloadAction<EditCommentFailurePayload>;

export interface DeleteCommentPayload {
  documentId: string;
  companyId: Id;
  commentId: string;
}

export type DeleteCommentAction = PayloadAction<DeleteCommentPayload>;

export interface DeleteCommentSuccessPayload {
  documentId: string;
  commentId: string;
}

export type DeleteCommentSuccessAction =
  PayloadAction<DeleteCommentSuccessPayload>;

export interface DeleteCommentFailurePayload {
  documentId: string;
  commentId: string;
  error: AppError;
}

export type DeleteCommentFailureAction =
  PayloadAction<DeleteCommentFailurePayload>;

const initialState: TransactionCommentsState = {
  comments: [],
  isLoading: true,
  isAdding: false,
  isEditing: false,
  isDeleting: false
};

const name = "features/transactions/comments";

const commentsSlice = createSlice({
  name,
  initialState,
  reducers: {
    loadComments: {
      reducer: (
        state: TransactionCommentsState,
        _action: LoadCommentsAction
      ) => {
        state.isLoading = true;
        state.error = undefined;
      },
      prepare: (documentId: string, companyId: Id) => ({
        payload: { documentId, companyId }
      })
    },
    loadCommentsSuccess: {
      reducer: (
        state: TransactionCommentsState,
        action: LoadCommentsSuccessAction
      ) => {
        state.comments = state.comments.filter(
          (comment) => comment.document_id !== action.payload.documentId
        );
        state.comments.push(
          ...action.payload.comments.map((comment) => ({
            ...comment,
            document_id: action.payload.documentId
          }))
        );
        state.isLoading = false;
      },
      prepare: (documentId: string, comments: Array<TransactionComment>) => ({
        payload: { documentId, comments }
      })
    },
    loadCommentsFailure: {
      reducer: (
        state: TransactionCommentsState,
        action: LoadCommentsFailureAction
      ) => {
        state.isLoading = false;
        state.error = action.payload.error;
      },
      prepare: (documentId: string, error: AppError) => ({
        payload: { documentId, error }
      })
    },
    addComment: {
      reducer: (state: TransactionCommentsState, _action: AddCommentAction) => {
        state.isAdding = true;
        state.error = undefined;
      },
      prepare: (documentId: string, companyId: Id, comment: string) => ({
        payload: { documentId, companyId, comment }
      })
    },
    addCommentSuccess: {
      reducer: (
        state: TransactionCommentsState,
        action: AddCommentSuccessAction
      ) => {
        state.comments.push({
          ...action.payload.comment,
          document_id: action.payload.documentId
        });
        state.isAdding = false;
      },
      prepare: (documentId: string, comment: TransactionComment) => ({
        payload: { documentId, comment }
      })
    },
    addCommentFailure: {
      reducer: (
        state: TransactionCommentsState,
        action: AddCommentFailureAction
      ) => {
        state.isAdding = false;
        state.error = action.payload.error;
      },
      prepare: (documentId: string, error: AppError) => ({
        payload: { documentId, error }
      })
    },
    editComment: {
      reducer: (state: TransactionCommentsState, action: EditCommentAction) => {
        state.isEditing = true;
        state.editCommentId = action.payload.commentId;
        state.error = undefined;
      },
      prepare: (
        documentId: string,
        companyId: Id,
        commentId: string,
        comment: string
      ) => ({
        payload: { documentId, companyId, commentId, comment }
      })
    },
    editCommentSuccess: {
      reducer: (
        state: TransactionCommentsState,
        action: EditCommentSuccessAction
      ) => {
        if (action.payload.comment) {
          const index = state.comments.findIndex(
            (comment) => comment?.id === action.payload.comment?.id
          );
          if (index !== -1) {
            state.comments[index] = {
              ...action.payload.comment,
              document_id: action.payload.documentId
            };
          } else {
            state.comments.push({
              ...action.payload.comment,
              document_id: action.payload.documentId
            });
          }
        }
        state.isEditing = false;
        state.editCommentId = undefined;
      },
      prepare: (documentId: string, comment: TransactionComment) => ({
        payload: { documentId, comment }
      })
    },
    editCommentFailure: {
      reducer: (
        state: TransactionCommentsState,
        action: EditCommentFailureAction
      ) => {
        state.isEditing = false;
        state.editCommentId = undefined;
        state.error = action.payload.error;
      },
      prepare: (documentId: string, commentId: string, error: AppError) => ({
        payload: { documentId, commentId, error }
      })
    },

    // Delete comments
    deleteComment: {
      reducer: (
        state: TransactionCommentsState,
        action: DeleteCommentAction
      ) => {
        state.isDeleting = true;
        state.deleteCommentId = action.payload.commentId;
        state.error = undefined;
      },
      prepare: (documentId: string, companyId: Id, commentId: string) => ({
        payload: { documentId, companyId, commentId }
      })
    },
    deleteCommentSuccess: {
      reducer: (
        state: TransactionCommentsState,
        action: DeleteCommentSuccessAction
      ) => {
        // Filter out the deleted comment immediately for smooth UI experience
        // We'll refetch all comments later, but this provides immediate feedback
        state.comments = state.comments.filter(
          (comment) => comment.id !== action.payload.commentId
        );
        state.isDeleting = false;
        state.deleteCommentId = undefined;
      },
      prepare: (documentId: string, commentId: string) => ({
        payload: { documentId, commentId }
      })
    },
    deleteCommentFailure: {
      reducer: (
        state: TransactionCommentsState,
        action: DeleteCommentFailureAction
      ) => {
        state.isDeleting = false;
        state.deleteCommentId = undefined;
        state.error = action.payload.error;
      },
      prepare: (documentId: string, commentId: string, error: AppError) => ({
        payload: { documentId, commentId, error }
      })
    }
  }
});

// Export actions
export const {
  loadComments,
  loadCommentsSuccess,
  loadCommentsFailure,
  addComment,
  addCommentSuccess,
  addCommentFailure,
  editComment,
  editCommentSuccess,
  editCommentFailure,
  deleteComment,
  deleteCommentSuccess,
  deleteCommentFailure
} = commentsSlice.actions;

// Selectors
export const selectTransactionComments =
  (documentId: string) =>
  (state: {
    features: { transactions: { comments: TransactionCommentsState } };
  }) =>
    state.features.transactions.comments.comments.filter(
      (comment) => comment.document_id === documentId
    );

export const selectIsLoadingComments = (state: {
  features: { transactions: { comments: TransactionCommentsState } };
}) => state.features.transactions.comments.isLoading;

export const selectIsAddingComment = (state: {
  features: { transactions: { comments: TransactionCommentsState } };
}) => state.features.transactions.comments.isAdding;

export const selectIsEditingComment = (state: {
  features: { transactions: { comments: TransactionCommentsState } };
}) => state.features.transactions.comments.isEditing;

export const selectIsDeletingComment = (state: {
  features: { transactions: { comments: TransactionCommentsState } };
}) => state.features.transactions.comments.isDeleting;

export const selectCommentsError = (state: {
  features: { transactions: { comments: TransactionCommentsState } };
}) => state.features.transactions.comments.error;

// Export reducer
export default commentsSlice.reducer;
